import { ROLE_LABEL } from '@qlk/shared';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { coQuyen, useDuongDanTrang, useToi } from '../../../lib/auth.js';
import {
  Avatar,
  DataTable,
  EmptyState,
  HopXacNhan,
  Nut,
  O,
  OChon,
  PageHeader,
  StatusBadge,
  ngayDay,
  ngayGio,
  type CotBang,
} from '../../../ui/index.js';
import { useHoSoLuuTru, useKhoiPhucThanhVien, useNhatKyThanhVien } from '../api.js';
import { ThongBaoMail } from '../components/KhoiLinkDatMatKhau.js';

interface DongNhatKy {
  id: string;
  action: string;
  module: string;
  entity: string;
  entityId: string | null;
  createdAt: string;
}

/**
 * Hồ sơ **chỉ xem** của một nhân sự đã nghỉ.
 *
 * Ba khối: thông tin tài khoản, "hồ sơ khi nghỉ" (ảnh chụp cố định lúc lưu trữ),
 * và toàn bộ nhật ký thao tác của người đó — lọc theo module và khoảng ngày, tải
 * thêm theo cursor.
 *
 * Không sửa được gì ở đây. Thao tác duy nhất là **Khôi phục**, và chỉ quản trị
 * thấy: nó đặt ngay dưới hồ sơ để người bấm đọc lý do nghỉ trước đã.
 */
export function TrangHoSoLuuTru() {
  const { id = '' } = useParams();
  const dieuHuong = useNavigate();
  const duongDan = useDuongDanTrang('QUẢN TRỊ');
  const toi = useToi();
  const laAdmin = coQuyen(toi.data?.permissions, 'team', 'admin');

  const hoSo = useHoSoLuuTru(id);
  const khoiPhuc = useKhoiPhucThanhVien();
  const [hoiKhoiPhuc, setHoiKhoiPhuc] = useState(false);

  const [module, setModule] = useState('');
  const [tu, setTu] = useState('');
  const [den, setDen] = useState('');
  const nhatKy = useNhatKyThanhVien(id, {
    ...(module ? { module } : {}),
    ...(tu ? { tu } : {}),
    ...(den ? { den } : {}),
  });

  const trang = nhatKy.data?.pages ?? [];
  const dong: DongNhatKy[] = trang.flatMap((t) => t.items);
  // Danh sách module lấy từ trang đầu: máy chủ tính trên **toàn bộ** nhật ký của
  // người này, không phải trên trang đang xem, nên ô lọc không bị hẹp dần.
  const cacModule = trang[0]?.modules ?? [];

  const cot: CotBang<DongNhatKy>[] = [
    { key: 'luc', header: 'Lúc', ghim: true, render: (r) => ngayGio(r.createdAt) },
    {
      key: 'module',
      header: 'Module',
      render: (r) => <StatusBadge tone="neutral">{r.module}</StatusBadge>,
    },
    {
      key: 'action',
      header: 'Thao tác',
      render: (r) => <code className="text-phu">{r.action}</code>,
    },
    {
      key: 'doiTuong',
      header: 'Đối tượng',
      render: (r) => (
        <span className="text-chu-phu">
          {r.entity}
          {r.entityId ? <span className="text-chu-mo"> · {r.entityId}</span> : null}
        </span>
      ),
    },
  ];

  if (hoSo.isError) {
    return (
      <>
        <PageHeader breadcrumb={duongDan} title="Hồ sơ nhân sự đã nghỉ" />
        <div className="rounded-the border border-vien bg-be-mat">
          <EmptyState loi title="Không mở được hồ sơ" description={hoSo.error.message} />
        </div>
      </>
    );
  }

  if (!hoSo.data) {
    return (
      <>
        <PageHeader breadcrumb={duongDan} title="Hồ sơ nhân sự đã nghỉ" />
        <div className="h-64 animate-pulse rounded-the border border-vien bg-be-mat" />
      </>
    );
  }

  const { member, snapshot, joinedAt } = hoSo.data;

  return (
    <>
      <PageHeader
        breadcrumb={[...duongDan, 'Lưu trữ']}
        title={member.name}
        description="Hồ sơ chỉ xem. Dữ liệu và nhật ký của người này giữ nguyên, không sửa được từ đây."
        actions={
          <Nut
            kieu="phu"
            onClick={() => {
              void dieuHuong('/nhan-su');
            }}
          >
            Về trang Nhân sự
          </Nut>
        }
      />

      <div className="grid grid-cols-1 gap-4 @min-[900px]/noi-dung:grid-cols-2">
        {/* ----------------------- Thông tin tài khoản ---------------------- */}
        <section className="rounded-the border border-vien bg-be-mat p-6">
          <div className="flex items-center gap-3">
            <Avatar name={member.name} color={member.avatarColor} size={44} />
            <div className="min-w-0">
              <p className="truncate text-tieu-de-khoi font-semibold text-chu">{member.name}</p>
              <p className="truncate text-co-ban text-chu-phu">{member.email}</p>
            </div>
            <StatusBadge tone="neutral">Đã nghỉ</StatusBadge>
          </div>

          <dl className="mt-5 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-co-ban">
            <dt className="text-chu-phu">Ngày tham gia</dt>
            <dd className="text-chu">{ngayDay(joinedAt)}</dd>

            <dt className="text-chu-phu">Lưu trữ lúc</dt>
            <dd className="text-chu">{member.archivedAt ? ngayGio(member.archivedAt) : '—'}</dd>

            <dt className="text-chu-phu">Người lưu trữ</dt>
            <dd className="text-chu">{member.archivedByName ?? '—'}</dd>

            <dt className="text-chu-phu">Lý do</dt>
            <dd className="text-chu">{member.archiveReason ?? '—'}</dd>

            <dt className="text-chu-phu">Hoạt động cuối</dt>
            <dd className="text-chu">
              {member.lastActiveAt ? ngayGio(member.lastActiveAt) : 'Chưa đăng nhập'}
            </dd>
          </dl>
        </section>

        {/* ------------------------- Hồ sơ khi nghỉ ------------------------- */}
        <section className="rounded-the border border-vien bg-be-mat p-6">
          <h2 className="text-tieu-de-khoi font-semibold text-chu">Hồ sơ khi nghỉ</h2>
          <p className="mt-1 text-phu text-chu-mo">
            Ảnh chụp cố định lúc lưu trữ. Kênh bên dưới có thể đã sang người khác.
          </p>

          {snapshot ? (
            <>
              <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-co-ban">
                <dt className="text-chu-phu">Vai trò</dt>
                <dd className="text-chu">
                  {snapshot.roles.map((k) => ROLE_LABEL[k]).join(' · ') || '—'}
                </dd>
              </dl>

              <p className="mt-4 text-phu font-medium text-chu-phu">
                Kênh đang phụ trách ({snapshot.channels.length})
              </p>
              {snapshot.channels.length === 0 ? (
                <p className="mt-1 text-co-ban text-chu-mo">Không phụ trách kênh nào.</p>
              ) : (
                <ul className="mt-1 space-y-1 text-co-ban text-chu">
                  {snapshot.channels.map((k) => (
                    <li key={k.id}>
                      {k.name} <span className="text-chu-mo">· {k.role}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <p className="mt-4 text-co-ban text-chu-mo">
              Bản ghi này được lưu trữ trước khi có ảnh chụp hồ sơ, nên không có dữ liệu.
            </p>
          )}

          {laAdmin && (
            <div className="mt-5 rounded-[10px] border border-vien bg-nen p-3">
              <p className="text-phu font-medium text-chu-phu">Nhân sự quay lại?</p>
              <p className="mt-1 text-phu text-chu-mo">
                Khôi phục đưa tài khoản về trạng thái hoạt động và gửi link đặt lại mật khẩu. Kênh
                cũ <strong className="text-chu">không</strong> tự gán lại.
              </p>

              {khoiPhuc.data ? (
                <ThongBaoMail
                  ketQua={khoiPhuc.data.datLaiMatKhau}
                  tieuDe="Đã khôi phục và tạo link đặt mật khẩu"
                />
              ) : (
                <Nut
                  kieu="chinh"
                  className="mt-3"
                  onClick={() => {
                    setHoiKhoiPhuc(true);
                  }}
                >
                  Khôi phục tài khoản
                </Nut>
              )}
            </div>
          )}
        </section>
      </div>

      {/* ------------------------- Nhật ký thao tác ------------------------- */}

      <section className="mt-6">
        <h2 className="mb-3 text-tieu-de-khoi font-semibold text-chu">Nhật ký thao tác</h2>

        <div className="mb-3 flex flex-wrap items-end gap-3">
          <OChon
            hienNhan
            label="Module"
            value={module}
            onChange={setModule}
            options={[
              { value: '', label: 'Tất cả module' },
              ...cacModule.map((m) => ({ value: m, label: m })),
            ]}
          />
          <O label="Từ ngày" type="date" value={tu} onChange={setTu} />
          <O label="Đến ngày" type="date" value={den} onChange={setDen} />
        </div>

        {nhatKy.isError ? (
          <div className="rounded-the border border-vien bg-be-mat">
            <EmptyState loi title="Không tải được nhật ký" description={nhatKy.error.message} />
          </div>
        ) : (
          <DataTable
            caption={`Nhật ký thao tác của ${member.name}`}
            columns={cot}
            rows={dong}
            keyOf={(r) => r.id}
            dangTai={nhatKy.isPending}
            conNua={nhatKy.hasNextPage}
            dangTaiThem={nhatKy.isFetchingNextPage}
            onTaiThem={() => {
              void nhatKy.fetchNextPage();
            }}
            khiRong={
              <EmptyState
                title="Không có thao tác nào"
                description="Người này chưa làm gì được ghi nhật ký, hoặc bộ lọc đang quá hẹp."
              />
            }
          />
        )}
      </section>

      {hoiKhoiPhuc && (
        <HopXacNhan
          title="Khôi phục tài khoản?"
          nhanXacNhan="Khôi phục"
          dangChay={khoiPhuc.isPending}
          loi={khoiPhuc.error?.message ?? null}
          onXacNhan={() => {
            khoiPhuc.mutate(
              { id },
              {
                onSuccess: () => {
                  setHoiKhoiPhuc(false);
                },
              },
            );
          }}
          onDong={() => {
            setHoiKhoiPhuc(false);
          }}
        >
          <p>
            <strong className="text-chu">{member.name}</strong> ({member.email}) sẽ đăng nhập lại
            được sau khi đặt mật khẩu mới.
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Hệ thống gửi link đặt lại mật khẩu tới email của người này; mật khẩu cũ hết dùng.
            </li>
            <li>
              Kênh cũ <strong className="text-chu">không</strong> tự gán lại — hãy giao kênh bằng
              tay sau khi khôi phục.
            </li>
            <li>Vai trò giữ như lúc nghỉ; đổi lại trong hộp thoại sửa thành viên nếu cần.</li>
          </ul>
        </HopXacNhan>
      )}
    </>
  );
}
